import React, { useContext, useEffect, useState } from "react";
import { Link, redirect, useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  PasswordInput,
  Text,
  TextInput,
  Paper,
  Group,
  Stack,
  Container,
  Title,
  Alert,
  Center,
  PinInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { motion } from "framer-motion";
import {
  IconMail,
  IconLock,
  IconArrowRight,
  IconCheck,
  IconAlertCircle,
  IconPhone,
  IconMail as IconMailAlert,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import {
  Colors,
  HttpErrorCodesObj,
  HttpSuccessCodesObj,
  ContactInformation,
  ValidOtpLength,
} from "../../Lib/data";
import { AllFatherContext } from "../../App";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getBrowser,
  getDevice,
  getIpAddress,
  parseString,
  validateEmail,
} from "../../Lib/methods";
import { NonPaginatedResponse } from "../../Lib/responses";
import { getSiteName } from "../../Lib/siteContent";
import { LoginResponse } from "../../Lib/types";
import { PerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";
import LanguageSelector from "../../Lib/i18n/LanguageSelector";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get("redirect");
  const impersonationToken = searchParams.get("token");

  interface EmailAndPasswordFormProps {
    email: string;
    password: string;
  }

  interface OtpFormProps {
    otp: string;
    email: string;
  }

  const emailAndPasswordForm = useForm<EmailAndPasswordFormProps>({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) => (validateEmail(value) ? null : t("auth.email")),
      password: (value) => (value && value.length >= 6 ? null : t("auth.password")),
    },
  });

  const context = useContext(AllFatherContext);
  const siteName = getSiteName(context?.assets);
  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [isRequestingOtp, setRequestingOtp] = useState<boolean>(false);

  const otpForm = useForm<OtpFormProps>({
    initialValues: {
      otp: "",
      email: "",
    },
    validate: {
      otp: (value) =>
        isOtpSent ? (value && value.length === ValidOtpLength ? null : t("auth.otp")) : null,
      email: (value) => (validateEmail(value) ? null : t("auth.email")),
    },
  });

  const [currentLoginScreen, setCurrentLoginScreen] = useState<"emailAndPassword" | "otp">("otp");

  const [otpTokenID, setOtpTokenID] = useState<string | null>(null);
  const [isKeepStudentLoggedIn, setKeepStudentLoggedIn] = useState<boolean>(false);
  const [isLoggingIn, setLoggingIn] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isAccountSuspended, setIsAccountSuspended] = useState<boolean>(false);

  useEffect(() => {
    if (impersonationToken) {
      localStorage.setItem("t", impersonationToken);
      navigate(redirect || "/d/overview", { replace: true });
    }
  }, [impersonationToken, navigate, redirect]);

  // IF THE USER SPECIFIES KEEP LOGGED IN, GENERATE LONGER LASTING TOKEN
  const LoginStudentWithEmailAndPassword = async () => {
    const { hasErrors: formHasErrors } = emailAndPasswordForm.validate();
    if (!formHasErrors) {
      const { email, password } = emailAndPasswordForm.values;
      setLoggingIn(true);
      const browser = getBrowser();
      const device = getDevice();
      const ipAddress = await getIpAddress();

      const r = await PerformRequest<NonPaginatedResponse<LoginResponse>>({
        method: Endpoints.GetStudentLoginWithEmailAndPassword().method,
        route: Endpoints.GetStudentLoginWithEmailAndPassword().route,
        data: {
          email: parseString(email, true),
          password: password,
          browser: browser,
          device: device,
          ip: ipAddress,
          isKeepUserLoggedIn: isKeepStudentLoggedIn,
        },
        callback: () => {
          setLoggingIn(false);
        },
      });
      setLoggingIn(false);
      if (r) {
        if (r.status === HttpSuccessCodesObj.Success) {
          localStorage.setItem("t", r.data.data.token);
          if (redirect) {
            navigate(redirect);
          } else {
            navigate("/d/courses");
          }
        } else if (r.status === HttpErrorCodesObj.NotFound) {
          DefaultErrorNotification(t("auth.userNotFound"));
        } else if (r.status === HttpErrorCodesObj.Unauthorized) {
          DefaultErrorNotification(t("auth.invalidPassword"));
        } else if (r.status === HttpErrorCodesObj.BadRequest) {
          // Account Suspended
          setIsAccountSuspended(true);
          DefaultErrorNotification(t("auth.accountSuspended"));
        }
      }
    }
  };

  const RequestOtp = async () => {
    const { hasErrors: formHasErrors } = otpForm.validate();
    if (!formHasErrors) {
      const { email } = otpForm.values;
      setRequestingOtp(true);
      const r = await PerformRequest<NonPaginatedResponse<{ tokenID: string }>>({
        method: Endpoints.GetStudentLoginWithOtpSend().method,
        route: Endpoints.GetStudentLoginWithOtpSend().route,
        data: {
          email: email,
        },
        callback: () => {
          setRequestingOtp(false);
        },
      });
      setRequestingOtp(false);
      if (r) {
        if (r.status === HttpSuccessCodesObj.Created) {
          setIsOtpSent(true);
          setOtpTokenID(r?.data?.data?.tokenID);
          DefaultSuccessNotification(t("auth.otpSent"));
        } else {
          DefaultErrorNotification(r.message);
        }
      }
    }
  };
  const ValidateOtpAndLogin = async () => {
    const { hasErrors: formHasErrors } = otpForm.validate();
    if (!formHasErrors) {
      const { email, otp } = otpForm.values;
      setLoggingIn(true);
      const r = await PerformRequest<NonPaginatedResponse<LoginResponse>>({
        method: Endpoints.GetStudentLoginWithOtpValidate(otpTokenID ?? "").method,
        route: Endpoints.GetStudentLoginWithOtpValidate(otpTokenID ?? "").route,
        data: {
          email: email,
          otp: otp,
          device: getDevice(),
          browser: getBrowser(),
          ip: await getIpAddress(),
          isKeepUserLoggedIn: isKeepStudentLoggedIn,
        },
        callback: () => {
          setLoggingIn(false);
        },
      });
      setLoggingIn(false);
      if (r) {
        if (r.status === HttpSuccessCodesObj.Success) {
          localStorage.setItem("t", r.data.data.token);
          if (redirect) {
            navigate(redirect);
          } else {
            navigate("/d/courses");
          }
        } else if (r.status === HttpErrorCodesObj.NotFound) {
          DefaultErrorNotification(t("auth.userNotFound"));
        } else if (r.status === HttpErrorCodesObj.Unauthorized) {
          DefaultErrorNotification(t("auth.invalidPassword"));
        } else if (r.status === HttpErrorCodesObj.BadRequest) {
          // Account Suspended
          setIsAccountSuspended(true);
          DefaultErrorNotification(t("auth.accountSuspended"));
        }
      }
    }
  };

  return (
    <div className="login-page">
      <div style={{ position: "fixed", top: 24, right: 24, zIndex: 2000 }}>
        <LanguageSelector />
      </div>
      {/* Background Elements */}
      <div className="background-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <Container size="sm" className="login-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="login-content"
        >
          {/* Logo Section */}
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="logo-section"
          >
            <img src={context?.assets?.logos?.legacy} alt={siteName} className="logo" />
            <Title order={1} className="app-title">
              {siteName}
            </Title>
            <Text className="welcome-text" size="lg" c="dimmed">
              {t("auth.welcomeBack")}
            </Text>
          </motion.div>

          {/* Login Form */}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {currentLoginScreen === "emailAndPassword" && (
              <Paper className="login-form" radius="lg" p="xl" withBorder>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    LoginStudentWithEmailAndPassword();
                  }}
                >
                  <Stack gap="lg">
                    {/* Email Input */}
                    <TextInput
                      leftSection={<IconMail size={16} />}
                      label={t("auth.email")}
                      placeholder={t("auth.email")}
                      size="md"
                      radius="md"
                      variant="filled"
                      {...emailAndPasswordForm.getInputProps("email")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          LoginStudentWithEmailAndPassword();
                        }
                      }}
                      classNames={{
                        input: "form-input",
                        label: "form-label",
                      }}
                    />

                    {/* Password Input */}
                    <PasswordInput
                      leftSection={<IconLock size={16} />}
                      label={t("auth.password")}
                      placeholder={t("auth.password")}
                      size="md"
                      radius="md"
                      variant="filled"
                      visible={showPassword}
                      onVisibilityChange={setShowPassword}
                      {...emailAndPasswordForm.getInputProps("password")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          LoginStudentWithEmailAndPassword();
                        }
                      }}
                      classNames={{
                        input: "form-input",
                        label: "form-label",
                        visibilityToggle: "password-toggle",
                      }}
                    />

                    {/* Remember Me & Forgot Password */}
                    <Group justify="space-between" align="center">
                      <Checkbox
                        checked={isKeepStudentLoggedIn}
                        onChange={(e) => setKeepStudentLoggedIn(e.target.checked)}
                        label={t("auth.rememberMe")}
                      />
                      <Text
                        component={Link}
                        to="/forgot-password"
                        className="forgot-password-link"
                        size="sm"
                      >
                        {t("auth.forgotPassword")}
                      </Text>
                    </Group>
                    <Center>
                      <Text
                        onClick={() => setCurrentLoginScreen("otp")}
                        className="pointer"
                        size="sm"
                      >
                        {t("auth.useOtp")}
                      </Text>
                    </Center>
                    {/* Login Button */}
                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      radius="md"
                      loading={isLoggingIn}
                      className="login-button"
                      rightSection={
                        isLoggingIn ? <IconCheck size={16} /> : <IconArrowRight size={16} />
                      }
                    >
                      {isLoggingIn ? t("auth.login") : t("auth.signIn")}
                    </Button>
                  </Stack>
                </form>

                {/* Sign Up Link */}
                <Text ta="center" mt="lg" className="signup-text">
                  {t("auth.dontHaveAccount")} &nbsp;
                  <Text component={Link} to="/sign-up" className="signup-link" span fw={600}>
                    {t("auth.signUp")}
                  </Text>
                </Text>
              </Paper>
            )}
            {currentLoginScreen === "otp" && (
              <Paper className="login-form" radius="lg" p="xl" withBorder>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!isOtpSent) {
                      RequestOtp();
                    } else {
                      ValidateOtpAndLogin();
                    }
                  }}
                >
                  <Stack gap="lg">
                    {/* Email Input */}
                    <TextInput
                      leftSection={<IconMail size={16} />}
                      label={t("auth.email")}
                      placeholder={t("auth.email")}
                      size="md"
                      radius="md"
                      disabled={isOtpSent}
                      variant="filled"
                      {...otpForm.getInputProps("email")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          RequestOtp();
                        }
                      }}
                      classNames={{
                        input: "form-input",
                        label: "form-label",
                      }}
                    />

                    {/* Otp Input */}
                    {isOtpSent && (
                      <>
                        <Text fz="sm" fw={500}>
                          {t("auth.otpLabel")}
                        </Text>
                        <Center mt={-10}>
                          <PinInput
                            length={ValidOtpLength}
                            {...otpForm.getInputProps("otp")}
                            size="md"
                            classNames={{
                              input: "form-input",
                              // label: "form-label",
                            }}
                          />
                        </Center>
                      </>
                    )}

                    {/* Remember Me & Forgot Password */}
                    <Group justify="space-between" align="center">
                      <Checkbox
                        checked={isKeepStudentLoggedIn}
                        onChange={(e) => setKeepStudentLoggedIn(e.target.checked)}
                        label={t("auth.rememberMe")}
                      />
                      <Text
                        onClick={() => setCurrentLoginScreen("emailAndPassword")}
                        className="pointer"
                        size="sm"
                      >
                        {t("auth.usePassword")}
                      </Text>
                    </Group>

                    {/* Login Button */}
                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      radius="md"
                      loading={isRequestingOtp || isLoggingIn}
                      className="login-button"
                      rightSection={
                        isRequestingOtp || isLoggingIn ? (
                          <IconCheck size={16} />
                        ) : (
                          <IconArrowRight size={16} />
                        )
                      }
                    >
                      {!isOtpSent ? t("auth.requestOtp") : t("auth.login")}
                    </Button>
                  </Stack>
                </form>

                {/* Sign Up Link */}
                <Text ta="center" mt="lg" className="signup-text">
                  {t("auth.dontHaveAccount")} &nbsp;
                  <Text component={Link} to="/sign-up" className="signup-link" span fw={600}>
                    {t("auth.signUp")}
                  </Text>
                </Text>
              </Paper>
            )}
          </motion.div>
          {/* Account Suspended Alert */}
          {isAccountSuspended && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              style={{ marginBottom: "1rem" }}
            >
              <Alert
                icon={<IconAlertCircle size="1rem" />}
                title="Account Suspended"
                color="red"
                variant="light"
                withCloseButton
                onClose={() => setIsAccountSuspended(false)}
              >
                <Stack gap="xs">
                  <Text size="sm">
                    Your account has been suspended. Please contact the administrator for
                    assistance.
                  </Text>
                  <Group gap="xs" mt="xs">
                    <IconMailAlert size={14} />
                    <Text size="sm" fw={500}>
                      {ContactInformation.email}
                    </Text>
                  </Group>
                </Stack>
              </Alert>
            </motion.div>
          )}
        </motion.div>
      </Container>
    </div>
  );
};

export default Login;
